import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import {
  fetchStaffsForStaffReserve,
  type StaffForStaffReserve,
} from '../../../utils/reservation/by-staff/fetchStaffsForStaffReserve';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';

import ReserveStepHeader from '../ReserveStepHeader';
import StaffAccordionCard from '../StaffAccordionCard';
import { useI18n } from '../../../i18n';

const SelectStaffByStaff: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';

  const {
    storeId,
    skippedStoreSelectionByStaff,
    selectedDate,
    selectedStartTime,
    selectedServices,
    setSelectedStaffId,
    setSelectedStaffName,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedServices,
    setIsFreeReservation,
  } = useReserveContext();

  const [staffs, setStaffs] = useState<StaffForStaffReserve[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [expandedStaffId, setExpandedStaffId] = useState<number | null>(null);

  const serviceIds = useMemo(
    () =>
      selectedServices
        .map((service) => Number(service.serviceId))
        .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0),
    [selectedServices],
  );

  useEffect(() => {
    if (!storeId) {
      navigate('/reserve/by-staff/store');
      return;
    }
    if (isEditing && (!selectedDate || !selectedStartTime || serviceIds.length === 0)) {
      toast.info(t('staff.returnAfterServiceDateTime'));
      navigate('/reserve/by-staff/confirm-reservation');
      return;
    }

    const load = async () => {
      setLoading(true);
      setExpandedStaffId(null);
      try {
        if (isEditing) {
          const data = await fetchStaffOptionsForDate({
            storeId,
            date: selectedDate!,
            startTime: selectedStartTime!,
            serviceIds,
          });
          setStaffs(
            data.staffs.map((staff) => ({
              id: staff.staff_id,
              nickname: staff.nickname || staff.name,
              icon_url: staff.icon_url,
              bio: staff.bio || null,
              role_name: staff.role_name || null,
              sns_links: staff.sns_links,
            })),
          );
          return;
        }

        const data = await fetchStaffsForStaffReserve(storeId);
        setStaffs(data);
      } catch (err) {
        console.error(err);
        toast.error(t('staff.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [storeId, isEditing, selectedDate, selectedStartTime, serviceIds, navigate, t]);

  const handleSelectStaff = async (staff: StaffForStaffReserve) => {
    if (updating) return;

    const displayName = staff.nickname || '';

    if (isEditing) {
      if (!selectedDate || !selectedStartTime || serviceIds.length === 0) {
        toast.info(t('staff.returnAfterServiceDateTime'));
        navigate('/reserve/by-staff/confirm-reservation');
        return;
      }

      setUpdating(true);
      try {
        const durations = await fetchStaffServiceDurations(staff.id, serviceIds);
        const durationMap = new Map(
          durations.map((row) => [Number(row.service_id), Number(row.duration_minutes)]),
        );

        setSelectedStaffId(staff.id);
        setSelectedStaffName(displayName);
        setSelectedServices(
          selectedServices.map((service) => ({
            ...service,
            duration_minutes:
              durationMap.get(Number(service.serviceId)) ?? Number(service.duration_minutes) ?? 0,
            staffId: staff.id,
            staffName: displayName,
          })),
        );
        navigate('/reserve/by-staff/confirm-reservation');
      } catch (err: any) {
        console.error(err);
        toast.error(err?.message || t('staff.updateFailed'));
      } finally {
        setUpdating(false);
      }
      return;
    }

    setSelectedStaffId(staff.id);
    setSelectedStaffName(displayName);

    // ✅ スタッフ変更＝後続はリセット（by-date思想と同じ）
    setSelectedServices([]);
    setSelectedDate(null);
    setSelectedStartTime(null);
    setIsFreeReservation(false);

    navigate('/reserve/by-staff/service');
  };

  const toggleExpandedStaff = (staffId: number) => {
    setExpandedStaffId((prev) => (prev === staffId ? null : staffId));
  };

  return (
    <div className="space-y-4">
      <ReserveStepHeader
        title={t('staff.chooseTitle')}
        onBack={() =>
          navigate(
            isEditing
              ? '/reserve/by-staff/confirm-reservation'
              : skippedStoreSelectionByStaff
                ? '/reserve'
                : '/reserve/by-staff/store',
          )
        }
        step={2}
        totalSteps={5}
      />

      {loading ? (
        <p className="text-center text-slate-400 text-sm">
          {isEditing ? t('staff.loadingAvailable') : t('staff.loadingMembers')}
        </p>
      ) : staffs.length === 0 ? (
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-rose-100 p-2 text-rose-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-700">{t('staff.cannotChange')}</div>
              <div className="mt-1 text-sm leading-6 text-rose-600">
                {t('staff.noAlternative')}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {staffs.map((s, index) => {
            const displayName = s.nickname || t('staff.fallbackName');

            return (
              <StaffAccordionCard
                key={s.id}
                name={displayName}
                roleName={s.role_name}
                bio={s.bio}
                iconUrl={s.icon_url}
                snsLinks={s.sns_links}
                isExpanded={expandedStaffId === s.id}
                onToggle={() => toggleExpandedStaff(s.id)}
                onAction={() => handleSelectStaff(s)}
                actionLabel={
                  isEditing
                    ? t('staff.changeTo', { name: displayName })
                    : t('staff.bookWith', { name: displayName })
                }
                actionEyebrow={isEditing ? t('staff.changeEyebrow') : t('staff.bookingEyebrow')}
                toneIndex={index}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SelectStaffByStaff;
